/**
 * ==========================================================================
 *  LianeBestellWidget.jsx — Bestell-Widget fuer Liane-Pflegebox
 * ==========================================================================
 *  Oeffentliches Widget (kein Auth) — zwei Schritte:
 *    1. Konfiguration: Produkte auswaehlen, Budget-Bar bis 42 EUR, plus
 *       separate Sektion fuer wiederverwendbaren Bettschutz (max. 4/Jahr).
 *    2. Antrag: Pflegebeduerftige Person, Adresse, Kontaktperson,
 *       Pflichthaekchen, Unterschrift, Absenden.
 *
 *  Produkte kommen live aus dem Cockpit-Katalog (GET /api/liane/public/produkte).
 *
 *  Variantenlogik:
 *    - Material-Varianten (z.B. Nitril/Vinyl/Latex bei Handschuhen) werden
 *      als separate Karten in der linken Spalte angezeigt.
 *    - Bei Produkten mit nur 1-2 Material-Varianten ohne Groessen
 *      (z.B. Desinfektion mit/ohne Alkohol) zeigen wir alternativ
 *      Inline-Buttons in einer einzigen Karte.
 *    - Groessen-Auswahl (z.B. S/M/L/XL) erfolgt im Warenkorb pro Zeile.
 * ==========================================================================
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

const CI = {
  brombeer: '#85366C',
  budget: { unter50: '#f59e0b', halb: '#2563eb', voll: '#16a34a' },
};

const BUDGET_42 = 42.00;
const MAX_BETTSCHUTZ_JAHR = 4;

export default function LianeBestellWidget() {
  const [produkte, setProdukte] = useState([]);
  const [pflegekassen, setPflegekassen] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [schritt, setSchritt] = useState('konfiguration'); // 'konfiguration' | 'antrag' | 'erfolg'

  // Warenkorb: Array von { key, produkt, variante, anzahl }
  //   key = eindeutiger Schluessel pro Zeile (mehrere Eintraege pro Variante moeglich)
  //   anzahl = Stueck (1 = eine Verpackungseinheit, also z.B. eine Box mit 100 Stk)
  const [warenkorb, setWarenkorb] = useState([]);

  // Antrags-Formular-State
  const [form, setForm] = useState({
    anrede: '', vorname: '', nachname: '',
    geb_tag: '', geb_monat: '', geb_jahr: '',
    krankenkasse: '', versicherungsart: 'GKV', versichertennummer: '',
    beihilfe_berechtigt: false, pflegegrad: '',
    strasse: '', hausnummer: '', plz: '', stadt: '',
    lieferadresse_abweichend: false,
    liefer_vorname: '', liefer_nachname: '',
    liefer_strasse: '', liefer_hausnummer: '',
    liefer_plz: '', liefer_stadt: '',
    kontakt_vorname: '', kontakt_nachname: '',
    kontakt_email: '', kontakt_telefon: '',
    bedarf_bekannt: false, anbieterwechsel: false, datenschutz_akzeptiert: false,
  });
  const [unterschriftDataURL, setUnterschriftDataURL] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [prodRes, kassenRes] = await Promise.all([
          fetch('/api/liane/public/produkte'),
          fetch('/api/pflegekassen/public'),
        ]);
        if (!prodRes.ok) throw new Error('Produktkatalog konnte nicht geladen werden.');
        const prodData = await prodRes.json();
        setProdukte(Array.isArray(prodData) ? prodData : []);
        if (kassenRes.ok) {
          const kassenData = await kassenRes.json();
          setPflegekassen(Array.isArray(kassenData) ? kassenData : []);
        }
      } catch (e) {
        setFehler(e.message || 'Unbekannter Fehler');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // --- Produkt-Gruppierung -----------------------------------------------
  // Materialien gruppieren: ein Produkt mit Materialien wird in pro-Material-
  // Karten gerendert (z.B. Handschuhe Nitril / Vinyl / Latex). Innerhalb der
  // Material-Gruppe gibt es ggf. mehrere Groessen.
  const produktKarten = useMemo(() => {
    const karten = [];
    for (const p of produkte) {
      const varianten = p.varianten || [];
      const materialien = [...new Set(varianten.map(v => v.material || '').map(s => s.trim()))];
      const hatVerschMaterial = materialien.length > 1;
      const hatGroessen = varianten.some(v => (v.groesse || '').trim() !== '');

      if (!hatVerschMaterial) {
        // Eine einzige Karte. Dropdown im Warenkorb fuer Groesse, falls vorhanden.
        karten.push({
          karte_id: p.id,
          produkt: p,
          material_filter: null,
          inline_material_buttons: null,
          varianten,
        });
        continue;
      }

      // Mehrere Materialien:
      // - wenn KEINE Groessen vorhanden sind UND <= 3 Materialien: Inline-Buttons
      //   in einer Karte (z.B. "mit Alkohol" / "ohne Alkohol")
      // - sonst: pro Material eine separate Karte
      if (!hatGroessen && materialien.length <= 3) {
        karten.push({
          karte_id: p.id,
          produkt: p,
          material_filter: null,
          inline_material_buttons: materialien,
          varianten,
        });
      } else {
        for (const m of materialien) {
          const varGrp = varianten.filter(v => (v.material || '').trim() === m);
          karten.push({
            karte_id: `${p.id}::${m}`,
            produkt: p,
            material_filter: m,
            inline_material_buttons: null,
            varianten: varGrp,
            material_titel: m || '',
          });
        }
      }
    }
    return karten;
  }, [produkte]);

  // 42-EUR-Topf-Karten zuerst, jaehrlich-4-Karten ans Ende
  const karten42 = produktKarten.filter(k => k.produkt.topf !== 'jaehrlich_4_stueck');
  const kartenBettschutz = produktKarten.filter(k => k.produkt.topf === 'jaehrlich_4_stueck');

  // --- Warenkorb-Berechnung ---------------------------------------------
  const summe42 = warenkorb
    .filter(z => z.produkt.topf !== 'jaehrlich_4_stueck')
    .reduce((s, z) => s + (Number(z.variante?.vk_preis_brutto) || 0) * z.anzahl, 0);
  const summeBettschutz = warenkorb
    .filter(z => z.produkt.topf === 'jaehrlich_4_stueck')
    .reduce((s, z) => s + z.anzahl, 0);
  const budgetProzent = Math.min(100, Math.round((summe42 / BUDGET_42) * 100));
  const budgetFarbe =
    budgetProzent >= 100 ? CI.budget.voll
    : budgetProzent >= 50 ? CI.budget.halb
    : CI.budget.unter50;

  // Was wuerde passieren, wenn man EINE weitere Einheit von Variante v hinzufuegt?
  const wuerdeBudgetSprengen = (variante, topf) => {
    if (topf === 'jaehrlich_4_stueck') return summeBettschutz >= MAX_BETTSCHUTZ_JAHR;
    const preis = Number(variante?.vk_preis_brutto) || 0;
    return (summe42 + preis) > BUDGET_42 + 0.001;
  };

  // --- Warenkorb-Aktionen -----------------------------------------------
  const addWarenkorb = (produkt, variante) => {
    if (!variante) return;
    // Eine neue Warenkorb-Zeile pro Klick — falls man unterschiedliche
    // Groessen will, kann man im Warenkorb pro Zeile umstellen.
    setWarenkorb(prev => [
      ...prev,
      {
        key: 'wk_' + Math.random().toString(36).slice(2),
        produkt,
        variante,
        anzahl: 1,
      },
    ]);
  };
  const removeWarenkorb = (key) => setWarenkorb(prev => prev.filter(z => z.key !== key));
  const setAnzahl = (key, anzahl) => {
    if (anzahl < 1) { removeWarenkorb(key); return; }
    setWarenkorb(prev => prev.map(z => z.key === key ? { ...z, anzahl } : z));
  };
  const setVariante = (key, varianteId) => {
    setWarenkorb(prev => prev.map(z => {
      if (z.key !== key) return z;
      const neueV = (z.produkt.varianten || []).find(v => v.id === varianteId);
      return neueV ? { ...z, variante: neueV } : z;
    }));
  };

  // --- Formular-Aktionen ------------------------------------------------
  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const handleAbsenden = async () => {
    if (!form.datenschutz_akzeptiert) {
      alert('Bitte den Datenschutzhinweis akzeptieren.');
      return;
    }
    if (!form.vorname.trim() || !form.nachname.trim()) {
      alert('Bitte Vor- und Nachname der pflegebeduerftigen Person eingeben.');
      return;
    }
    if (!unterschriftDataURL) {
      alert('Bitte unterschreiben Sie den Antrag.');
      return;
    }
    // V-Nr Validierung fuer GKV (1 Buchstabe + 9 Ziffern). PKV-Nummern sind frei.
    if (form.versicherungsart === 'GKV' && form.versichertennummer
        && !/^[A-Z]\d{9}$/.test(form.versichertennummer)) {
      alert('Die Krankenversichertennummer muss 10-stellig sein (1 Buchstabe + 9 Ziffern).');
      return;
    }
    setSubmitLoading(true);
    try {
      const geb = (form.geb_tag && form.geb_monat && form.geb_jahr)
        ? `${form.geb_jahr.padStart(4, '0')}-${form.geb_monat.padStart(2, '0')}-${form.geb_tag.padStart(2, '0')}`
        : null;
      const body = {
        anrede: form.anrede || null,
        vorname: form.vorname.trim(),
        nachname: form.nachname.trim(),
        geburtsdatum: geb,
        krankenkasse: form.krankenkasse || null,
        versicherungsart: form.versicherungsart || null,
        versichertennummer: form.versichertennummer || null,
        beihilfe_berechtigt: !!form.beihilfe_berechtigt,
        pflegegrad: form.pflegegrad ? Number(form.pflegegrad) : null,
        strasse: form.strasse || null,
        hausnummer: form.hausnummer || null,
        plz: form.plz || null,
        stadt: form.stadt || null,
        lieferadresse_abweichend: !!form.lieferadresse_abweichend,
        liefer_vorname: form.liefer_vorname || null,
        liefer_nachname: form.liefer_nachname || null,
        liefer_strasse: form.liefer_strasse || null,
        liefer_hausnummer: form.liefer_hausnummer || null,
        liefer_plz: form.liefer_plz || null,
        liefer_stadt: form.liefer_stadt || null,
        kontakt_vorname: form.kontakt_vorname || null,
        kontakt_nachname: form.kontakt_nachname || null,
        kontakt_email: form.kontakt_email || null,
        kontakt_telefon: form.kontakt_telefon || null,
        bedarf_bekannt: !!form.bedarf_bekannt,
        anbieterwechsel: !!form.anbieterwechsel,
        datenschutz_akzeptiert: !!form.datenschutz_akzeptiert,
        unterschrift_png: unterschriftDataURL,
        warenkorb: warenkorb.map(z => ({
          produkt_id: z.produkt.id,
          variante_id: z.variante.id,
          anzahl: z.anzahl,
        })),
      };
      const res = await fetch('/api/liane/public/bestellung', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setSchritt('erfolg');
    } catch (e) {
      alert('Senden fehlgeschlagen: ' + (e.message || 'unbekannt'));
    } finally {
      setSubmitLoading(false);
    }
  };

  if (isLoading) {
    return <div className="p-10 text-center text-gray-500">Lade Produktkatalog…</div>;
  }
  if (fehler) {
    return <div className="p-10 text-center text-red-700">{fehler}</div>;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f1ede4' }}>
      {/* Schritt-Indikator */}
      <SchrittIndikator schritt={schritt} />

      {schritt === 'konfiguration' && (
        <KonfigurationsView
          karten42={karten42}
          kartenBettschutz={kartenBettschutz}
          warenkorb={warenkorb}
          addWarenkorb={addWarenkorb}
          removeWarenkorb={removeWarenkorb}
          setAnzahl={setAnzahl}
          setVariante={setVariante}
          summe42={summe42}
          summeBettschutz={summeBettschutz}
          budgetProzent={budgetProzent}
          budgetFarbe={budgetFarbe}
          wuerdeBudgetSprengen={wuerdeBudgetSprengen}
          onWeiter={() => {
            if (warenkorb.length === 0) {
              alert('Bitte zuerst Produkte auswaehlen.');
              return;
            }
            setSchritt('antrag');
          }}
        />
      )}

      {schritt === 'antrag' && (
        <AntragsView
          form={form}
          setF={setF}
          pflegekassen={pflegekassen}
          unterschriftDataURL={unterschriftDataURL}
          setUnterschriftDataURL={setUnterschriftDataURL}
          submitLoading={submitLoading}
          onZurueck={() => setSchritt('konfiguration')}
          onAbsenden={handleAbsenden}
          summe42={summe42}
          warenkorb={warenkorb}
        />
      )}

      {schritt === 'erfolg' && <ErfolgView />}
    </div>
  );
}


// ==========================================================================
// Schritt-Indikator (Konfiguration ✓ → Antrag)
// ==========================================================================
function SchrittIndikator({ schritt }) {
  const s1Done = schritt !== 'konfiguration';
  const s2Active = schritt === 'antrag' || schritt === 'erfolg';
  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-center gap-4">
        <Stufe nummer="1" label="Konfiguration" done={s1Done} active={!s1Done} />
        <div className="h-0.5 w-20 bg-gray-300" />
        <Stufe nummer="2" label="Antrag" done={schritt === 'erfolg'} active={s2Active && schritt !== 'erfolg'} />
      </div>
    </div>
  );
}
function Stufe({ nummer, label, done, active }) {
  const farbe = done || active ? CI.brombeer : '#9ca3af';
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
        style={{ backgroundColor: farbe }}
      >
        {done ? '✓' : nummer}
      </div>
      <span style={{ color: farbe }} className="font-bold text-sm">{label}</span>
    </div>
  );
}


// ==========================================================================
// Schritt 1: Produktauswahl + Warenkorb
// ==========================================================================
function KonfigurationsView({ karten42, kartenBettschutz, warenkorb, addWarenkorb,
                              removeWarenkorb, setAnzahl, setVariante,
                              summe42, summeBettschutz, budgetProzent, budgetFarbe,
                              wuerdeBudgetSprengen, onWeiter }) {
  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Linke Spalte: Produktauswahl */}
        <div>
          <h2 className="text-center font-bold text-gray-800 mb-4">Produkte auswählen</h2>
          <div className="space-y-2">
            {karten42.map(k => (
              <ProduktKarte
                key={k.karte_id}
                karte={k}
                disabled={k.varianten.every(v => wuerdeBudgetSprengen(v, k.produkt.topf))}
                wuerdeBudgetSprengen={wuerdeBudgetSprengen}
                onAdd={addWarenkorb}
              />
            ))}
          </div>
        </div>

        {/* Rechte Spalte: Warenkorb */}
        <div>
          <h2 className="text-center font-bold text-gray-800 mb-4">Ihre Liane-Pflegebox</h2>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            {/* Budget-Bar */}
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-bold text-gray-500">Kostenfreies 42-€-Budget</span>
                <span className="text-sm font-bold" style={{ color: budgetFarbe }}>{budgetProzent} %</span>
              </div>
              <div className="h-3 bg-gray-200 rounded overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{ width: `${budgetProzent}%`, backgroundColor: budgetFarbe }}
                />
              </div>
              <div className="text-xs text-gray-500 mt-1 text-right">
                {summe42.toFixed(2)} € von {BUDGET_42.toFixed(2)} €
              </div>
            </div>

            {/* Hinweis */}
            {budgetProzent < 100 && warenkorb.some(z => z.produkt.topf !== 'jaehrlich_4_stueck') && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 text-sm font-medium" style={{ color: budgetFarbe }}>
                ⚠ Kostenfreien Höchstbetrag nicht erreicht.
              </div>
            )}
            {warenkorb.length === 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3 text-sm text-gray-500 flex items-center gap-2">
                <span className="text-gray-400">ℹ</span>
                <span>Noch keine Produkte ausgewählt.</span>
              </div>
            )}

            {/* Warenkorb-Eintraege (42-EUR-Topf) */}
            <div className="space-y-2">
              {warenkorb.filter(z => z.produkt.topf !== 'jaehrlich_4_stueck').map(z => (
                <WarenkorbZeile
                  key={z.key}
                  zeile={z}
                  onRemove={() => removeWarenkorb(z.key)}
                  onAnzahl={(n) => setAnzahl(z.key, n)}
                  onVariante={(vid) => setVariante(z.key, vid)}
                />
              ))}
            </div>

            {/* Separator + Bettschutz */}
            {warenkorb.some(z => z.produkt.topf === 'jaehrlich_4_stueck') && (
              <>
                <div className="border-t-2 border-dashed border-gray-300 my-4 pt-3">
                  <div className="text-xs font-bold text-gray-500 uppercase mb-2">
                    Wiederverwendbarer Bettschutz · max. {MAX_BETTSCHUTZ_JAHR}/Jahr · ausserhalb 42-€-Topf
                  </div>
                  <div className="space-y-2">
                    {warenkorb.filter(z => z.produkt.topf === 'jaehrlich_4_stueck').map(z => (
                      <WarenkorbZeile
                        key={z.key}
                        zeile={z}
                        onRemove={() => removeWarenkorb(z.key)}
                        onAnzahl={(n) => setAnzahl(z.key, n)}
                        onVariante={(vid) => setVariante(z.key, vid)}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Extra Schutz fuer 0 EUR — wiederverwendbare Bettschutzeinlage */}
      {kartenBettschutz.length > 0 && (
        <div className="mt-8 max-w-3xl mx-auto">
          <div className="text-center mb-2">
            <div className="inline-block px-6 py-2 rounded-md font-bold text-white" style={{ backgroundColor: CI.budget.voll }}>
              Extra Schutz für 0 €
            </div>
          </div>
          {kartenBettschutz.map(k => (
            <BettschutzKarte
              key={k.karte_id}
              karte={k}
              warenkorb={warenkorb}
              summeBettschutz={summeBettschutz}
              onAdd={() => {
                if (summeBettschutz < MAX_BETTSCHUTZ_JAHR) {
                  addWarenkorb(k.produkt, k.varianten[0]);
                }
              }}
              onRemove={() => {
                const letzte = [...warenkorb].reverse().find(z => z.produkt.id === k.produkt.id);
                if (letzte) removeWarenkorb(letzte.key);
              }}
            />
          ))}
        </div>
      )}

      <div className="text-center mt-8 pb-8">
        <button
          onClick={onWeiter}
          disabled={warenkorb.length === 0}
          className="px-10 py-3 rounded-xl text-white font-bold text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-transform hover:scale-105"
          style={{ backgroundColor: '#ea580c' }}
        >
          Weiter &raquo;
        </button>
      </div>
    </div>
  );
}


// ==========================================================================
// Produkt-Karte (links): Bild + Titel + Verpackung + Plus-Button
// ==========================================================================
function ProduktKarte({ karte, disabled, wuerdeBudgetSprengen, onAdd }) {
  const { produkt, varianten, inline_material_buttons, material_filter, material_titel } = karte;
  const [aktivesMaterial, setAktivesMaterial] = useState(
    inline_material_buttons ? inline_material_buttons[0] : null
  );

  // Welche Variante wird beim "+"-Klick hinzugefuegt?
  let zielVariante = varianten[0];
  if (inline_material_buttons && aktivesMaterial) {
    zielVariante = varianten.find(v => (v.material || '').trim() === aktivesMaterial) || varianten[0];
  }

  const verpackung = zielVariante?.verpackungseinheit || '';
  const bildUrl = produkt.bilder?.thumbnail || produkt.bilder?.haupt || null;
  const titel = material_titel ? `${produkt.titel} (${material_titel})` : produkt.titel;

  return (
    <div
      className={`bg-white rounded-xl p-3 border border-gray-200 flex items-center gap-3 transition-opacity ${
        disabled ? 'opacity-40' : ''
      }`}
    >
      {bildUrl ? (
        <img src={bildUrl} alt={produkt.titel} className="w-14 h-14 object-contain flex-shrink-0" />
      ) : (
        <div className="w-14 h-14 bg-gray-100 rounded flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-bold text-gray-800 truncate">{titel}</div>
        {verpackung && (
          <span className="inline-block text-xs font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded mt-1">
            {verpackung}
          </span>
        )}
        {inline_material_buttons && (
          <div className="flex gap-1 mt-2">
            {inline_material_buttons.map(m => (
              <button
                key={m}
                onClick={() => setAktivesMaterial(m)}
                className={`text-[11px] font-bold px-2 py-1 rounded border ${
                  aktivesMaterial === m
                    ? 'bg-fuchsia-100 border-fuchsia-300 text-fuchsia-800'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-fuchsia-200'
                }`}
              >
                {m || '—'}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={() => onAdd(produkt, zielVariante)}
        disabled={disabled || !zielVariante}
        className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-2xl font-bold flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed transition-transform hover:scale-105"
        style={{ backgroundColor: '#22c55e' }}
        title="Zur Box hinzufuegen"
      >+</button>
    </div>
  );
}


// ==========================================================================
// Warenkorb-Zeile (rechts): Bild + Titel + Variantenwahl + Menge + Entfernen
// ==========================================================================
function WarenkorbZeile({ zeile, onRemove, onAnzahl, onVariante }) {
  const { produkt, variante, anzahl } = zeile;
  const bildUrl = produkt.bilder?.thumbnail || null;
  // Liste der Varianten innerhalb desselben Materials (falls Groessenwahl noetig)
  const sameMat = (produkt.varianten || []).filter(
    v => (v.material || '').trim() === (variante.material || '').trim()
  );
  const hatGroessenWahl = sameMat.length > 1;

  return (
    <div className="bg-white rounded-lg p-2 border border-gray-200 flex items-center gap-2">
      {bildUrl ? (
        <img src={bildUrl} alt={produkt.titel} className="w-10 h-10 object-contain flex-shrink-0" />
      ) : (
        <div className="w-10 h-10 bg-gray-100 rounded flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-bold text-gray-800 text-sm truncate">{produkt.titel}</div>
        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
          {variante.material && (
            <span className="text-[10px] font-medium text-gray-500">{variante.material}</span>
          )}
          {variante.verpackungseinheit && (
            <span className="text-[10px] font-medium bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              {variante.verpackungseinheit}
            </span>
          )}
          {hatGroessenWahl && (
            <select
              value={variante.id}
              onChange={(e) => onVariante(e.target.value)}
              className="text-[11px] border border-gray-200 rounded px-1 py-0.5 bg-white"
            >
              {sameMat.map(v => (
                <option key={v.id} value={v.id}>
                  {v.groesse || '(eine Groesse)'}
                </option>
              ))}
            </select>
          )}
          {Number(variante.vk_preis_brutto) > 0 && (
            <span className="text-[10px] text-gray-400 ml-auto">
              {Number(variante.vk_preis_brutto).toFixed(2)} €
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onAnzahl(anzahl - 1)}
          className="w-7 h-7 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">−</button>
        <span className="font-bold text-sm w-6 text-center">{anzahl}</span>
        <button onClick={() => onAnzahl(anzahl + 1)}
          className="w-7 h-7 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">+</button>
      </div>
      <button onClick={onRemove}
        className="w-7 h-7 rounded border border-gray-300 text-gray-400 hover:text-red-600 hover:bg-red-50"
        title="Entfernen"
      >×</button>
    </div>
  );
}


// ==========================================================================
// Bettschutz-Karte (unten, ausserhalb des Budgets)
// ==========================================================================
function BettschutzKarte({ karte, warenkorb, summeBettschutz, onAdd, onRemove }) {
  const { produkt } = karte;
  const bildUrl = produkt.bilder?.haupt || produkt.bilder?.thumbnail || null;
  const aktuell = warenkorb.filter(z => z.produkt.id === produkt.id)
    .reduce((s, z) => s + z.anzahl, 0);
  return (
    <div className="bg-white rounded-xl p-6 border-2 border-emerald-100 mt-2 grid md:grid-cols-2 gap-6 items-center">
      <div className="flex justify-center">
        {bildUrl ? (
          <img src={bildUrl} alt={produkt.titel} className="max-h-40 object-contain" />
        ) : (
          <div className="w-32 h-32 bg-gray-100 rounded" />
        )}
      </div>
      <div>
        <h3 className="font-bold text-gray-800 mb-2">{produkt.titel}</h3>
        {produkt.beschreibung && (
          <p className="text-sm text-gray-600 mb-3">{produkt.beschreibung}</p>
        )}
        <div className="font-bold text-gray-700 mb-2">max. {MAX_BETTSCHUTZ_JAHR} Stück pro Jahr</div>
        <div className="flex items-center gap-3">
          <button
            onClick={onRemove}
            disabled={aktuell === 0}
            className="w-9 h-9 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >−</button>
          <span className="font-bold w-16 text-center">{aktuell} Stück</span>
          <button
            onClick={onAdd}
            disabled={summeBettschutz >= MAX_BETTSCHUTZ_JAHR}
            className="w-9 h-9 rounded flex items-center justify-center text-white font-bold disabled:opacity-30"
            style={{ backgroundColor: '#22c55e' }}
          >+</button>
        </div>
      </div>
    </div>
  );
}


// ==========================================================================
// Schritt 2: Antragsformular
// ==========================================================================
function AntragsView({ form, setF, pflegekassen, unterschriftDataURL, setUnterschriftDataURL,
                       submitLoading, onZurueck, onAbsenden, summe42, warenkorb }) {
  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h2 className="text-center text-xl font-bold text-gray-800 mb-6">Angaben zum Pflegebedürftigen</h2>

      {/* Pflegebeduerftige Person */}
      <Block titel="Pflegebedürftige Person">
        <div className="flex gap-6 mb-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={form.anrede === 'Herr'} onChange={() => setF('anrede', 'Herr')} /> Herr
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={form.anrede === 'Frau'} onChange={() => setF('anrede', 'Frau')} /> Frau
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <Input label="Vorname*" value={form.vorname} onChange={v => setF('vorname', v)} />
          <Input label="Nachname*" value={form.nachname} onChange={v => setF('nachname', v)} />
        </div>
        <label className="text-xs font-bold text-gray-500 block mb-1">Geburtsdatum*</label>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <input type="text" placeholder="Tag" maxLength={2} value={form.geb_tag} onChange={e => setF('geb_tag', e.target.value.replace(/\D/g, ''))}
            className="px-3 py-2 border-b border-gray-300 bg-transparent text-sm" />
          <input type="text" placeholder="Monat" maxLength={2} value={form.geb_monat} onChange={e => setF('geb_monat', e.target.value.replace(/\D/g, ''))}
            className="px-3 py-2 border-b border-gray-300 bg-transparent text-sm" />
          <input type="text" placeholder="Jahr" maxLength={4} value={form.geb_jahr} onChange={e => setF('geb_jahr', e.target.value.replace(/\D/g, ''))}
            className="px-3 py-2 border-b border-gray-300 bg-transparent text-sm" />
        </div>
        <VersicherungsBlock form={form} setF={setF} pflegekassen={pflegekassen} />
        <label className="text-xs font-bold text-gray-500 block mb-2 mt-3">Pflegegrad*</label>
        <div className="flex gap-2">
          {[1,2,3,4,5].map(n => (
            <label key={n} className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded text-sm cursor-pointer hover:bg-gray-50">
              <input type="radio" checked={String(form.pflegegrad) === String(n)} onChange={() => setF('pflegegrad', String(n))} />
              {n}
            </label>
          ))}
        </div>
      </Block>

      {/* Adresse */}
      <Block titel="Adresse" sub="Adresse des Pflegebedürftigen">
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <div className="md:col-span-2"><Input label="Straße*" value={form.strasse} onChange={v => setF('strasse', v)} /></div>
          <Input label="Hausnummer*" value={form.hausnummer} onChange={v => setF('hausnummer', v)} />
        </div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <Input label="PLZ*" value={form.plz} onChange={v => setF('plz', v.replace(/\D/g, '').slice(0, 5))} />
          <Input label="Stadt*" value={form.stadt} onChange={v => setF('stadt', v)} />
        </div>
        <label className="flex items-center gap-2 text-sm bg-white border border-gray-200 rounded p-3">
          <input type="checkbox" checked={form.lieferadresse_abweichend} onChange={e => setF('lieferadresse_abweichend', e.target.checked)} />
          Abweichende Lieferadresse
        </label>
        {form.lieferadresse_abweichend && (
          <div className="mt-3 space-y-3 bg-gray-50 p-3 rounded">
            <div className="grid md:grid-cols-2 gap-3">
              <Input label="Vorname" value={form.liefer_vorname} onChange={v => setF('liefer_vorname', v)} />
              <Input label="Nachname" value={form.liefer_nachname} onChange={v => setF('liefer_nachname', v)} />
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-2"><Input label="Straße" value={form.liefer_strasse} onChange={v => setF('liefer_strasse', v)} /></div>
              <Input label="Hausnummer" value={form.liefer_hausnummer} onChange={v => setF('liefer_hausnummer', v)} />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Input label="PLZ" value={form.liefer_plz} onChange={v => setF('liefer_plz', v.replace(/\D/g, '').slice(0, 5))} />
              <Input label="Stadt" value={form.liefer_stadt} onChange={v => setF('liefer_stadt', v)} />
            </div>
          </div>
        )}
      </Block>

      <h2 className="text-center text-xl font-bold text-gray-800 mb-6 mt-8">Angaben zur Kontaktperson</h2>

      <Block titel="Kontaktperson" sub="Wen sollen wir bei Fragen zu dem Antrag kontaktieren?">
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <Input label="Vorname*" value={form.kontakt_vorname} onChange={v => setF('kontakt_vorname', v)} />
          <Input label="Nachname*" value={form.kontakt_nachname} onChange={v => setF('kontakt_nachname', v)} />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Input label="E-Mail*" type="email" value={form.kontakt_email} onChange={v => setF('kontakt_email', v)} />
          <Input label="Telefon*" value={form.kontakt_telefon} onChange={v => setF('kontakt_telefon', v)} />
        </div>
      </Block>

      <div className="space-y-2 mb-6">
        <CheckBoxZeile checked={form.bedarf_bekannt} onChange={v => setF('bedarf_bekannt', v)}>
          Hiermit bestätige ich, dass ich meinen Bedarf kenne und keine Beratung über die Pflegehilfsmittel benötige.
        </CheckBoxZeile>
        <CheckBoxZeile checked={form.anbieterwechsel} onChange={v => setF('anbieterwechsel', v)}>
          Sollte bereits ein Vertrag zur Lieferung bestehen, so beauftrage ich TEUS UG mit der Kündigung des Vertrages meines bisherigen Anbieters mit dem heutigen Datum.
        </CheckBoxZeile>
        <CheckBoxZeile checked={form.datenschutz_akzeptiert} onChange={v => setF('datenschutz_akzeptiert', v)}>
          Ich habe die <a href="https://liane-pflegebox.de/datenschutz" target="_blank" rel="noreferrer" className="underline" style={{ color: CI.brombeer }}>Datenschutzerklärung</a> und <a href="https://liane-pflegebox.de/agb" target="_blank" rel="noreferrer" className="underline" style={{ color: CI.brombeer }}>AGB</a> gelesen und akzeptiere diese.
          <br />Zusätzlich erlaube ich die zwecksgebundene Weitergabe der gemachten persönlichen und gesundheitlichen Daten im Rahmen der Bestellung einer Liane-Pflegebox an den Dienstleister zur Erfüllung des Auftrags.
        </CheckBoxZeile>
      </div>

      <h2 className="text-center text-xl font-bold text-gray-800 mb-3">Unterschrift</h2>
      <SignaturePad onSave={setUnterschriftDataURL} initial={unterschriftDataURL} />

      <div className="flex gap-3 justify-between mt-8 pb-8">
        <button onClick={onZurueck}
          className="px-6 py-3 rounded-xl font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50">
          « Zurück
        </button>
        <button onClick={onAbsenden}
          disabled={submitLoading}
          className="px-10 py-3 rounded-xl font-bold text-white shadow-lg disabled:opacity-50"
          style={{ backgroundColor: '#ea580c' }}>
          {submitLoading ? 'Sende…' : 'Pflegebox beantragen'}
        </button>
      </div>
    </div>
  );
}

// Versicherungs-Block (GKV/PKV + Pflegekasse + V-Nr + Beihilfe).
// Logik 1:1 aus dem Buchungs-Widget der Pflegeberatung uebernommen, damit
// das Verhalten konsistent ist: Toggle-Buttons fuer Versicherungsart,
// Suchfeld mit Dropdown der Pflegekassen (gefiltert nach Typ), V-Nr-
// Validierung 1 Buchstabe + 9 Ziffern fuer GKV, frei fuer PKV.
function VersicherungsBlock({ form, setF, pflegekassen }) {
  const [insuranceSearch, setInsuranceSearch] = useState(form.krankenkasse || '');
  const [showInsuranceList, setShowInsuranceList] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowInsuranceList(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const gefiltert = pflegekassen.filter(k =>
    k.typ === form.versicherungsart &&
    k.name.toLowerCase().includes((insuranceSearch || '').toLowerCase())
  );

  const istVnrUngueltig = form.versicherungsart === 'GKV'
    && form.versichertennummer
    && !/^[A-Z]\d{9}$/.test(form.versichertennummer);

  return (
    <div className="mb-3">
      {/* Versicherungsart als Toggle */}
      <label className="text-xs font-bold text-gray-500 block mb-2">Kassen-Art*</label>
      <div className="flex gap-2 mb-3">
        {['GKV', 'PKV'].map(typ => (
          <button
            key={typ}
            type="button"
            onClick={() => {
              setF('versicherungsart', typ);
              setInsuranceSearch('');
              setF('krankenkasse', '');
            }}
            className={`flex-1 py-2 px-3 text-xs font-bold border rounded transition-colors ${
              form.versicherungsart === typ
                ? 'bg-fuchsia-700 text-white border-fuchsia-700'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            style={form.versicherungsart === typ ? { backgroundColor: '#85366C', borderColor: '#85366C' } : {}}
          >
            {typ === 'GKV' ? 'Gesetzlich (GKV)' : 'Privat (PKV)'}
          </button>
        ))}
      </div>

      {/* Pflegekassen-Suche mit Dropdown */}
      <div className="relative mb-3" ref={dropdownRef}>
        <label className="text-xs font-bold text-gray-500 block mb-1">
          Name der Pflegekasse*
        </label>
        <input
          type="text"
          placeholder="Bitte auswaehlen oder Name tippen…"
          value={insuranceSearch || form.krankenkasse}
          onChange={(e) => {
            setInsuranceSearch(e.target.value);
            setF('krankenkasse', e.target.value);
            setShowInsuranceList(true);
          }}
          onFocus={() => setShowInsuranceList(true)}
          className="w-full px-3 py-2 border-b border-gray-300 bg-transparent text-sm focus:outline-none focus:border-fuchsia-600"
        />
        {showInsuranceList && gefiltert.length > 0 && (
          <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
            {gefiltert.slice(0, 50).map((k) => (
              <li
                key={k.id}
                className="px-3 py-2 text-sm text-gray-700 hover:bg-fuchsia-50 cursor-pointer border-b border-gray-100"
                onClick={() => {
                  setF('krankenkasse', k.name);
                  setInsuranceSearch(k.name);
                  setShowInsuranceList(false);
                }}
              >
                {k.name}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Versichertennummer mit GKV-Validierung */}
      <div className="mb-3">
        <label className="text-xs font-bold text-gray-500 block mb-1 flex justify-between items-center">
          <span>Krankenversichertennummer*</span>
          {form.versicherungsart === 'GKV' && (
            <span className="text-[10px] text-gray-400 font-normal">10-stellig · 1 Buchstabe + 9 Ziffern</span>
          )}
        </label>
        <input
          type="text"
          value={form.versichertennummer}
          onChange={(e) => setF('versichertennummer',
            form.versicherungsart === 'GKV' ? e.target.value.toUpperCase() : e.target.value
          )}
          className={`w-full px-3 py-2 border-b text-sm focus:outline-none bg-transparent ${
            istVnrUngueltig ? 'border-red-500 text-red-700' : 'border-gray-300 focus:border-fuchsia-600'
          }`}
        />
        {istVnrUngueltig && (
          <div className="text-[11px] text-red-600 mt-1">
            Format ungueltig: erwartet wird 1 Buchstabe gefolgt von 9 Ziffern (z.B. A123456789).
          </div>
        )}
      </div>

      {/* Beihilfe-Berechtigung — fuer spaetere Abrechnungs-Logik */}
      <label className="flex items-center gap-2 text-sm bg-white border border-gray-200 rounded p-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.beihilfe_berechtigt}
          onChange={(e) => setF('beihilfe_berechtigt', e.target.checked)}
        />
        <span className="text-gray-700">
          Ich bin <strong>beihilfeberechtigt</strong> (z.B. Beamte, Pensionaere).
        </span>
      </label>
    </div>
  );
}


function Block({ titel, sub, children }) {
  return (
    <div className="bg-white/40 rounded-lg p-4 mb-4">
      <div className="font-bold text-gray-800 mb-1">{titel}</div>
      {sub && <div className="text-xs text-gray-500 mb-3">{sub}</div>}
      {children}
    </div>
  );
}
function Input({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <input
        type={type}
        placeholder={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border-b border-gray-300 bg-transparent text-sm focus:outline-none focus:border-fuchsia-600"
      />
    </div>
  );
}
function CheckBoxZeile({ checked, onChange, children }) {
  return (
    <label className="flex items-start gap-2 text-sm bg-white/60 border border-gray-200 rounded p-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="mt-1" />
      <div className="text-gray-700">{children}</div>
    </label>
  );
}


// ==========================================================================
// Unterschrift-Pad (HTML5 Canvas, Touch + Maus)
// ==========================================================================
function SignaturePad({ onSave, initial }) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const istZeichnend = useRef(false);
  const [istLeer, setIstLeer] = useState(!initial);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext('2d');
    ctx.scale(2, 2);
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 2;
    ctxRef.current = ctx;
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t?.clientX ?? e.clientX) - rect.left;
    const y = (t?.clientY ?? e.clientY) - rect.top;
    return { x, y };
  };
  const start = (e) => { e.preventDefault(); istZeichnend.current = true; const { x, y } = pos(e); ctxRef.current.beginPath(); ctxRef.current.moveTo(x, y); };
  const draw = (e) => { if (!istZeichnend.current) return; e.preventDefault(); const { x, y } = pos(e); ctxRef.current.lineTo(x, y); ctxRef.current.stroke(); setIstLeer(false); };
  const end = (e) => {
    if (!istZeichnend.current) return;
    e.preventDefault();
    istZeichnend.current = false;
    onSave(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const c = canvasRef.current;
    ctxRef.current.clearRect(0, 0, c.width, c.height);
    setIstLeer(true);
    onSave(null);
  };

  return (
    <div className="bg-white border-2 border-gray-300 rounded-lg p-3 relative">
      <canvas
        ref={canvasRef}
        className="w-full h-32 cursor-crosshair touch-none"
        onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
      />
      {istLeer && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-300 text-sm pointer-events-none">
          Hier mit Maus oder Finger unterschreiben
        </div>
      )}
      <button onClick={clear}
        className="absolute top-2 right-2 w-7 h-7 rounded-full border border-red-300 text-red-500 hover:bg-red-50 text-sm">
        ×
      </button>
    </div>
  );
}


// ==========================================================================
// Erfolgs-Bildschirm
// ==========================================================================
function ErfolgView() {
  return (
    <div className="max-w-2xl mx-auto px-6 py-20 text-center">
      <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-5xl text-emerald-600 mb-6">
        ✓
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Vielen Dank für Ihre Bestellung!</h2>
      <p className="text-gray-600 mb-2">
        Ihr Antrag für die Liane-Pflegebox ist bei uns eingegangen. Wir prüfen die Daten und reichen Ihren Antrag bei Ihrer Pflegekasse ein.
      </p>
      <p className="text-gray-600">
        Sie erhalten in Kürze eine Bestätigung per E-Mail. Bei Rückfragen melden wir uns telefonisch.
      </p>
    </div>
  );
}
